/**
 * Step 4 : Tarification
 * Options de paiement + Prix de vente + Prix de service
 */

import { useMemo, useEffect } from 'react';
import { WarrantyFormState } from '@/features/warranties/hooks/useWarrantyFormWizard';
import { useSociety, useChildrenHaveStripe } from '@/features/societies/hooks/useSocieties';
import { CreditCard } from 'lucide-react';
import { Tooltip } from '@/Components/ui/Tooltip';
import { CardSelectInput, type CardSelectOption } from '@/Components/shared/CardSelectInput';
import { FormInput } from '@/Components/shared/FormInput';
import { getWarrantyTypeWording } from '@/utils/vendorWording';
import { PricingSummaryCard } from '@/features/contracts/components/form-fields/PricingSummaryCard';
import { extractMonthlyFees, calculateMonthlyBreakdown } from '@/features/warranties/services/pricingCalculator';
import { useWarrantyPermissions } from '@/features/warranties/hooks/useWarrantyPermissions';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';

interface Step4Props {
  formData: WarrantyFormState;
  updateFormData: (updates: Partial<WarrantyFormState>) => void;
  onCanProceedChange: (canProceed: boolean) => void;
  showErrors?: boolean;
}

export const Step4Pricing = ({ formData, updateFormData, onCanProceedChange, showErrors = false }: Step4Props) => {
  const { canEditPrice } = useWarrantyPermissions(formData.societyId);
  const { data: effectiveConfig } = useEffectiveConfiguration(formData.societyId);
  const wording = useMemo(() => getWarrantyTypeWording(formData.warrantyType), [formData.warrantyType]);

  // Déterminer la société à vérifier pour le paiement en ligne
  const effectiveSocietyId = formData.societyId;

  // Récupérer la société effective
  const { data: effectiveSociety } = useSociety(effectiveSocietyId);

  // Vérifier si onlinePayment est activé
  const hasOnlinePayment = useMemo(() => !!effectiveSociety?.onlinePayment, [effectiveSociety?.onlinePayment]);

  // Vérifier si la société a au moins un mode de paiement Stripe dans sa config effective
  const hasStripePayment = useMemo(
    () => (effectiveConfig?.monthlyPayment ?? 0) >= 1 || (effectiveConfig?.onlinePayment ?? 0) >= 1,
    [effectiveConfig?.monthlyPayment, effectiveConfig?.onlinePayment],
  );

  // Vérifier si la société est warrantyPolicyOwner (elle gère les garanties pour le groupe)
  const isWarrantyPolicyOwner = useMemo(() => {
    const wpOwnerId = effectiveConfig?.warrantyPolicyOwnerId;
    return !wpOwnerId || wpOwnerId === formData.societyId;
  }, [effectiveConfig?.warrantyPolicyOwnerId, formData.societyId]);

  // Si la société n'a pas Stripe mais est warrantyPolicyOwner, vérifier si un enfant a Stripe
  const { data: childrenStripeData } = useChildrenHaveStripe(
    formData.societyId,
    { enabled: !hasStripePayment && isWarrantyPolicyOwner },
  );

  const showPaymentOptions = useMemo(
    () => hasStripePayment || (isWarrantyPolicyOwner && !!childrenStripeData?.hasChildWithStripe),
    [hasStripePayment, isWarrantyPolicyOwner, childrenStripeData?.hasChildWithStripe],
  );

  // Options de paiement dynamiques selon onlinePayment
  type PaymentOptionValue = 'cash_only' | 'both' | 'monthly_only';

  const paymentOptions = useMemo<CardSelectOption<PaymentOptionValue>[]>(() => [
    {
      label: hasOnlinePayment ? 'Comptant Stripe' : 'Comptant uniquement',
      value: 'cash_only',
      description: hasOnlinePayment
        ? 'Paiement comptant en ligne via Stripe'
        : 'Paiement comptant uniquement (sans paiement en ligne)',
    },
    {
      label: 'Comptant ou mensuel',
      value: 'both',
      description: 'Le client peut choisir entre paiement comptant ou mensualisation',
    },
    {
      label: 'Mensuel uniquement',
      value: 'monthly_only',
      description: 'Paiement mensualisé uniquement (via Stripe)',
    },
  ], [hasOnlinePayment]);

  // Dériver l'option sélectionnée depuis formData (pas de useEffect)
  const selectedPaymentOption = useMemo<PaymentOptionValue | null>(() => {
    if (formData.isCashPayment && !formData.isMensualisable) return 'cash_only';
    if (formData.isCashPayment && formData.isMensualisable) return 'both';
    if (!formData.isCashPayment && formData.isMensualisable) return 'monthly_only';
    return null;
  }, [formData.isCashPayment, formData.isMensualisable]);

  // Vérifier si le prix total est trop bas pour la mensualisation (minimum 200€)
  const isMensualTooLow = useMemo(() => {
    if (!formData.isMensualisable) return false;
    if (!formData.sellingPrice || formData.sellingPrice <= 0) return false;

    const totalPrice = selectedPaymentOption === 'monthly_only'
      ? formData.sellingPrice * (formData.duration || 0)
      : formData.sellingPrice;

    return totalPrice < 200;
  }, [formData.isMensualisable, formData.sellingPrice, formData.duration, selectedPaymentOption]);

  // Forcer comptant uniquement si les options de paiement Stripe ne sont pas disponibles
  useEffect(() => {
    if (!showPaymentOptions && (formData.isMensualisable || !formData.isCashPayment)) {
      updateFormData({ isCashPayment: true, isMensualisable: false });
    }
  }, [showPaymentOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler pour le changement d'option de paiement
  const handlePaymentOptionChange = (value: PaymentOptionValue) => {
    if (value === 'cash_only') {
      updateFormData({ isCashPayment: true, isMensualisable: false });
    } else if (value === 'both') {
      updateFormData({ isCashPayment: true, isMensualisable: true });
    } else if (value === 'monthly_only') {
      updateFormData({ isCashPayment: false, isMensualisable: true });
    }
  };

  // Le prix de la prestation Affinicar n'est pas un état du formulaire : il est résolu
  // côté API à la création, depuis le couple (société, durée). Ne PAS le pré-remplir ici —
  // l'ancien effet figeait le tarif de la première durée consultée, et un garde `useRef`
  // n'y pouvait rien, ce composant étant démonté à chaque changement d'étape.

  // Validation : tous les champs affichés sont obligatoires
  const isValid = useMemo(() => {
    // 0. Options de paiement : obligatoires
    if (formData.isCashPayment === undefined || formData.isMensualisable === undefined) return false;

    // 1. Prix de vente TTC : toujours obligatoire (0 autorisé)
    if (formData.sellingPrice === undefined || formData.sellingPrice === null || formData.sellingPrice < 0) return false;

    // 2. Minimum 200€ pour la mensualisation
    if (isMensualTooLow) return false;

    return true;
  }, [
    formData.isCashPayment,
    formData.isMensualisable,
    formData.sellingPrice,
    isMensualTooLow,
  ]);

  // Remonter la validité au container
  useEffect(() => {
    onCanProceedChange(isValid);
  }, [isValid, onCanProceedChange]);

  // Label dynamique pour le prix selon l'option de paiement
  const priceLabel = useMemo(() => {
    const isMonthlyOnly = formData.isMensualisable && !formData.isCashPayment;
    return isMonthlyOnly ? 'Prix mensuel TTC (€)' : 'Prix de vente TTC (€)';
  }, [formData.isMensualisable, formData.isCashPayment]);

  // Props pour le PricingSummaryCard selon l'option sélectionnée
  const pricingCardProps = useMemo(() => {
    if (isMensualTooLow) return null;
    if (!formData.sellingPrice || formData.sellingPrice <= 0) return null;
    if (!selectedPaymentOption) return null;

    const onlinePlateformFees = effectiveSociety?.onlinePlateformFees || 0;
    const monthlyPlateformFees = effectiveSociety?.monthlyPlateformFees || 0;
    const duration = formData.duration || 0;

    // Option 1 : Comptant uniquement
    if (selectedPaymentOption === 'cash_only') {
      if (!hasOnlinePayment) return null; // Pas de frais → pas de card
      const totalTTC = formData.sellingPrice;
      const totalHT = totalTTC / 1.2;
      const feeAmountTTC = ((totalHT * onlinePlateformFees) / 100) * 1.2;
      return {
        label: 'Prix de vente TTC',
        isMonthly: false,
        duration,
        totalTTC,
        monthlyClient: null,
        monthlySeller: null,
        feePercentage: onlinePlateformFees,
        feeAmountTTC,
      };
    }

    // Option 2 : Choix comptant/mensuel — géré par bothPricingCards
    if (selectedPaymentOption === 'both') return null;

    // Option 3 : Mensuel uniquement — frais inclus dans le prix saisi
    if (selectedPaymentOption === 'monthly_only') {
      if (!duration) return null;
      const monthlyClient = formData.sellingPrice;
      const monthlyFee = extractMonthlyFees(monthlyClient, monthlyPlateformFees);
      const monthlySeller = monthlyClient - monthlyFee;
      return {
        label: 'Prix mensuel client TTC',
        isMonthly: true,
        duration,
        totalTTC: monthlyClient * duration,
        monthlyClient,
        monthlySeller,
        feePercentage: monthlyPlateformFees,
        feeAmountTTC: monthlyFee,
      };
    }

    return null;
  }, [
    isMensualTooLow,
    formData.sellingPrice,
    formData.duration,
    selectedPaymentOption,
    hasOnlinePayment,
    effectiveSociety?.onlinePlateformFees,
    effectiveSociety?.monthlyPlateformFees,
  ]);

  // Props pour les 2 PricingSummaryCard côte à côte (option "Comptant ou mensuel")
  const bothPricingCards = useMemo(() => {
    if (isMensualTooLow) return null;
    if (selectedPaymentOption !== 'both') return null;
    if (!formData.sellingPrice || formData.sellingPrice <= 0) return null;
    const duration = formData.duration || 0;
    if (!duration) return null;

    const onlinePlateformFees = effectiveSociety?.onlinePlateformFees || 0;
    const monthlyPlateformFees = effectiveSociety?.monthlyPlateformFees || 0;

    // Carte gauche : comptant
    const totalTTC = formData.sellingPrice;
    const cashFeePercentage = hasOnlinePayment ? onlinePlateformFees : null;
    const cashFeeAmount = hasOnlinePayment
      ? ((totalTTC / 1.2) * onlinePlateformFees / 100) * 1.2
      : null;

    const cash = {
      label: 'Paiement comptant TTC',
      isMonthly: false,
      duration,
      totalTTC,
      monthlyClient: null,
      monthlySeller: null,
      feePercentage: cashFeePercentage,
      feeAmountTTC: cashFeeAmount,
    };

    // Carte droite : mensualisé (frais ajoutés, payés par le client)
    const breakdown = calculateMonthlyBreakdown(formData.sellingPrice, duration, monthlyPlateformFees);

    const monthly = {
      label: 'Paiement mensualisé TTC',
      isMonthly: true,
      duration,
      totalTTC: formData.sellingPrice,
      monthlyClient: breakdown.monthlyClient,
      monthlySeller: breakdown.monthlySeller,
      feePercentage: monthlyPlateformFees,
      feeAmountTTC: breakdown.monthlyFee,
    };

    return { cash, monthly };
  }, [
    isMensualTooLow,
    formData.sellingPrice,
    formData.duration,
    selectedPaymentOption,
    hasOnlinePayment,
    effectiveSociety?.onlinePlateformFees,
    effectiveSociety?.monthlyPlateformFees,
  ]);

  // Message de confirmation avec résumé de la garantie
  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-4">

      {/* Bloc unique: Tarification */}
      <div className="tw-relative tw-p-6 tw-space-y-5">

        {/* Titre de la step */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
          <CreditCard className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Tarification</h4>
        </div>

        {/* Section: Options de paiement (masquée si aucun paiement Stripe disponible) */}
        {showPaymentOptions && (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Choisissez les modes de paiement proposés
              <Tooltip content="Modes de paiement proposés au client pour ce contrat" />
            </p>
            <div id="payment-options-group">
              <CardSelectInput<PaymentOptionValue>
                options={paymentOptions}
                value={selectedPaymentOption}
                onChange={handlePaymentOptionChange}
                name="paymentOption"
                required
                columns={3}
              />
              {showErrors && !selectedPaymentOption && (
                <p className="tw-mt-1 tw-text-sm tw-text-red-600">Le mode de paiement est obligatoire</p>
              )}
            </div>
          </div>
        )}

        {/* Section: Prix */}
        <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
            Définissez le prix de vente {wording.deType}
            <Tooltip content="Prix facturé au client final, toutes taxes comprises" />
          </p>
          <div id="selling-price-input">
            <FormInput
              type="number"
              value={formData.sellingPrice !== undefined && formData.sellingPrice !== null ? String(formData.sellingPrice) : ''}
              onChange={(value) => updateFormData({ sellingPrice: value === '' ? undefined : parseFloat(value) })}
              label={priceLabel}
              placeholder="0.00"
              suffix="€"
              required
              disabled={!canEditPrice}
              error={showErrors && (formData.sellingPrice === undefined || formData.sellingPrice === null || formData.sellingPrice < 0) ? 'Le prix de vente est obligatoire' : undefined}
            />

            {/* Warning : prix total < 200€ avec mensualisation */}
            {isMensualTooLow && (
              <div className="tw-mt-3 tw-p-3 tw-bg-red-50 tw-rounded-md tw-border tw-border-red-300">
                <p className="tw-text-xs tw-text-red-800 tw-font-semibold tw-mb-0">
                  La mensualisation n'est pas disponible pour un prix total inférieur à 200€. Veuillez augmenter le prix ou choisir le paiement comptant uniquement.
                </p>
              </div>
            )}

            {/* Warning pour "Mensuel uniquement" */}
            {selectedPaymentOption === 'monthly_only' && !isMensualTooLow && (
              <div className="tw-mt-3 tw-p-3 tw-bg-amber-50 tw-rounded-md tw-border tw-border-amber-300">
                <p className="tw-text-xs tw-text-amber-800 tw-font-semibold tw-mb-0">
                  ⚠️ Attention : Vous devez renseigner le prix mensuel TTC, pas le prix total de {wording.withArticle}
                </p>
              </div>
            )}

            {/* Récapitulatif tarification — 2 cards côte à côte pour "Comptant ou mensuel" */}
            {bothPricingCards && (
              <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-4">
                <PricingSummaryCard {...bothPricingCards.cash} />
                <PricingSummaryCard {...bothPricingCards.monthly} />
              </div>
            )}

            {/* Récapitulatif tarification — card unique pour "Comptant" ou "Mensuel uniquement" */}
            {pricingCardProps && (
              <div className="tw-mt-4">
                <PricingSummaryCard {...pricingCardProps} />
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};
